// Contratos del blog entre admin, api y web.
//
// Una entrada de blog (v1: solo el tipo "news") tiene título y
// subtítulo trilingües, imágenes (destacada, thumbnail y la derivada
// para Open Graph) y un cuerpo por **bloques** estilo WordPress que se
// pueden añadir, reordenar y borrar. Cada bloque es de uno de tres
// tipos: cabecera (h3), texto (HTML saneado) o imagen.
//
// Semántica de idiomas: igual que colecciones, sin cascada al publicar
// la entidad (el DTO público proyecta el título al locale solicitado o
// devuelve 404). Dentro del cuerpo, cada bloque sí cae al primer idioma
// disponible para no dejar huecos en el artículo renderizado.
import { z } from 'zod';
import { IdSchema, ImageRefSchema, IsoDateSchema, SlugSchema } from './primitives.js';
import { LOCALES } from './i18n/locales.js';
import { PaginatedResponseSchema } from './pagination.js';
import { EventPublicItemSchema } from './events.js';

/**
 * Texto trilingüe con al menos una traducción (mismo contrato que el
 * label de colección). Lo usa el título: una entrada sin título en el
 * locale del visitante no se publica en ese locale.
 */
export const BlogTextSchema = z
  .object({
    es: z.string().min(1).optional(),
    ca: z.string().min(1).optional(),
    en: z.string().min(1).optional(),
  })
  .refine((v) => LOCALES.some((l) => typeof v[l] === 'string'), {
    message: 'At least one locale translation is required (es, ca or en)',
  });

/**
 * Texto trilingüe con todas las traducciones opcionales. Lo usan el
 * subtítulo y los textos de los bloques: durante la edición en borrador
 * un bloque puede quedar incompleto.
 */
export const BlogOptionalTextSchema = z.object({
  es: z.string().min(1).optional(),
  ca: z.string().min(1).optional(),
  en: z.string().min(1).optional(),
});

// Tipo de entrada. Solo "news" en la v1; literal para futureproof (al
// añadir un tipo nuevo se extiende el enum y se propaga).
export const BlogPostTypeSchema = z.enum(['news']);
export type BlogPostType = z.infer<typeof BlogPostTypeSchema>;

// Estado editorial: borrador (no visible en la web) o publicado.
export const BlogPostStatusSchema = z.enum(['draft', 'published']);
export type BlogPostStatus = z.infer<typeof BlogPostStatusSchema>;

// ---------------------------------------------------------------------------
// Bloques de contenido (modelo de edición / persistencia)
// ---------------------------------------------------------------------------

// Cada bloque lleva un `id` estable (nanoid generado en el cliente) que
// sirve de key en React y para asociar la imagen subida a su bloque al
// reordenar/borrar.
const BlockIdSchema = z.string().min(1);

// URL de destino de un enlace del cuerpo (CTA o imagen enlazada): ruta
// interna (/...) o http(s) absoluta. Sin esto, un editor (o una sesión
// admin comprometida) podría guardar `javascript:`/`data:` y ejecutarse
// en el navegador de cualquier visitante al hacer click — estos bloques
// se renderizan como `<a href>` sin pasar por ningún saneado (a
// diferencia del HTML de los bloques `text`, que sí se limpia con
// sanitize-html). `//host` empieza por `/` pero el navegador lo trata
// como URL protocol-relative hacia otro dominio, así que se excluye del
// caso "ruta interna": lo externo debe ir explícito por http(s)://.
const LinkUrlSchema = z.string().min(1).refine(
  (v) => (v.startsWith('/') && !v.startsWith('//')) || /^https?:\/\//i.test(v),
  'La URL debe ser una ruta interna (/...) o empezar por http(s)://',
);

// Cabecera: se renderiza como h3 en la web. Texto trilingüe.
export const BlogHeadingBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('heading'),
  text: BlogOptionalTextSchema,
});

// Texto enriquecido: HTML saneado (negrita, cursiva, links, saltos de
// línea) producido por el editor Tiptap del admin. Trilingüe.
export const BlogTextBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('text'),
  html: BlogOptionalTextSchema,
});

// Imagen del cuerpo: la propia imagen (opcional mientras es borrador y
// aún no se ha subido), su `alt` trilingüe, un pie opcional trilingüe y
// un enlace opcional (`href`): si está, la imagen se renderiza envuelta
// en un `<a>` (misma validación de URL que el CTA).
export const BlogImageBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('image'),
  image: ImageRefSchema.optional(),
  alt: BlogOptionalTextSchema.default({}),
  caption: BlogOptionalTextSchema.optional(),
  href: LinkUrlSchema.optional(),
  newTab: z.boolean().optional(),
});

// Colección: referencia por slug a una colección existente. Durante la
// edición se guarda solo el slug; al proyectar a público la API resuelve
// título, imagen, eventos y agregados desde la entidad real.
export const BlogCollectionBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('collection'),
  collectionSlug: SlugSchema,
});

// Artista: referencia por slug a un artista del Core (B2C). Al proyectar
// a público la API resuelve nombre, slug e imagen desde el Core.
export const BlogArtistBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('artist'),
  artistSlug: SlugSchema,
});

// Evento: referencia por slug a un evento del Core (B2C). Al proyectar
// a público la API resuelve los datos desde el Core.
export const BlogEventBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('event'),
  eventSlug: SlugSchema,
});

// CTA: botón con texto trilingüe y URL de destino. Sin resolución
// server-side (passthrough como heading/text).
export const BlogCtaBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('cta'),
  text: BlogOptionalTextSchema,
  url: LinkUrlSchema,
  newTab: z.boolean().optional().default(false),
});

// YouTube: vídeo embebido. Solo guardamos la URL; el front extrae el ID.
export const BlogYouTubeBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('youtube'),
  url: z.string().min(1),
});

// Spotify: música / podcast embebido. La URL se normaliza a formato embed.
export const BlogSpotifyBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('spotify'),
  url: z.string().min(1),
});

// X (Twitter): publicación embebida. Solo guardamos la URL; el front
// extrae usuario + id de status para montar el blockquote oficial.
export const BlogTwitterBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('twitter'),
  url: z.string().min(1),
});

// TikTok: vídeo embebido. Solo guardamos la URL; el front extrae
// usuario + id de vídeo para montar el blockquote oficial.
export const BlogTikTokBlockSchema = z.object({
  id: BlockIdSchema,
  type: z.literal('tiktok'),
  url: z.string().min(1),
});

export const BlogBlockSchema = z.discriminatedUnion('type', [
  BlogHeadingBlockSchema,
  BlogTextBlockSchema,
  BlogImageBlockSchema,
  BlogCollectionBlockSchema,
  BlogArtistBlockSchema,
  BlogEventBlockSchema,
  BlogCtaBlockSchema,
  BlogYouTubeBlockSchema,
  BlogSpotifyBlockSchema,
  BlogTwitterBlockSchema,
  BlogTikTokBlockSchema,
]);

export type BlogHeadingBlock = z.infer<typeof BlogHeadingBlockSchema>;
export type BlogTextBlock = z.infer<typeof BlogTextBlockSchema>;
export type BlogImageBlock = z.infer<typeof BlogImageBlockSchema>;
export type BlogCollectionBlock = z.infer<typeof BlogCollectionBlockSchema>;
export type BlogArtistBlock = z.infer<typeof BlogArtistBlockSchema>;
export type BlogEventBlock = z.infer<typeof BlogEventBlockSchema>;
export type BlogCtaBlock = z.infer<typeof BlogCtaBlockSchema>;
export type BlogYouTubeBlock = z.infer<typeof BlogYouTubeBlockSchema>;
export type BlogSpotifyBlock = z.infer<typeof BlogSpotifyBlockSchema>;
export type BlogTwitterBlock = z.infer<typeof BlogTwitterBlockSchema>;
export type BlogTikTokBlock = z.infer<typeof BlogTikTokBlockSchema>;
export type BlogBlock = z.infer<typeof BlogBlockSchema>;

// ---------------------------------------------------------------------------
// DTOs admin
// ---------------------------------------------------------------------------

/**
 * Imágenes destacadas de la entrada. `featured` es el hero (2.4:1),
 * `thumbnail` la de los listados/home, `og` la derivada para meta tags
 * (1200×630). Las tres se suben/derivan por endpoint propio.
 */
export const BlogImagesSchema = z.object({
  featured: ImageRefSchema.optional(),
  thumbnail: ImageRefSchema.optional(),
  og: ImageRefSchema.optional(),
});
export type BlogImages = z.infer<typeof BlogImagesSchema>;

/**
 * Entrada completa que la API devuelve al admin (para editar y para
 * alimentar la previsualización). Incluye los bloques completos.
 */
export const BlogPostAdminItemSchema = z.object({
  id: IdSchema,
  slug: SlugSchema,
  type: BlogPostTypeSchema,
  status: BlogPostStatusSchema,
  title: BlogTextSchema,
  subtitle: BlogOptionalTextSchema,
  // Resumen corto (estilo "excerpt" de WordPress): alimenta las tarjetas
  // de listado/home y, si está, la meta description / descripción social.
  excerpt: BlogOptionalTextSchema,
  // Título SEO: sobrescribe el `<title>` y el og:title sin tocar el H1.
  seoTitle: BlogOptionalTextSchema,
  images: BlogImagesSchema,
  blocks: z.array(BlogBlockSchema),
  publishedAt: IsoDateSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  _etag: z.string().optional(),
});
export type BlogPostAdminItem = z.infer<typeof BlogPostAdminItemSchema>;

/**
 * Item del listado admin. Sin bloques (los listados no los necesitan)
 * para no transferir cuerpos enteros al pintar la tabla.
 */
export const BlogPostAdminListItemSchema = z.object({
  id: IdSchema,
  slug: SlugSchema,
  type: BlogPostTypeSchema,
  status: BlogPostStatusSchema,
  title: BlogTextSchema,
  thumbnail: ImageRefSchema.optional(),
  publishedAt: IsoDateSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  _etag: z.string().optional(),
});
export type BlogPostAdminListItem = z.infer<typeof BlogPostAdminListItemSchema>;

/**
 * Input de creación. El slug es único e inmutable tras crear. La entrada
 * nace en borrador; las imágenes se suben por endpoint propio.
 */
export const CreateBlogPostInputSchema = z.object({
  slug: SlugSchema,
  type: BlogPostTypeSchema.default('news'),
  title: BlogTextSchema,
  subtitle: BlogOptionalTextSchema.optional(),
  excerpt: BlogOptionalTextSchema.optional(),
  seoTitle: BlogOptionalTextSchema.optional(),
  blocks: z.array(BlogBlockSchema).default([]),
});
export type CreateBlogPostInput = z.infer<typeof CreateBlogPostInputSchema>;

/**
 * Input de update parcial. El slug se omite a propósito (inmutable). El
 * cambio de `status` controla publicar/despublicar; `publishedAt` se
 * fija solo en la primera publicación y luego es editable a mano.
 */
export const UpdateBlogPostInputSchema = z
  .object({
    title: BlogTextSchema.optional(),
    subtitle: BlogOptionalTextSchema.optional(),
    excerpt: BlogOptionalTextSchema.optional(),
    seoTitle: BlogOptionalTextSchema.optional(),
    blocks: z.array(BlogBlockSchema).optional(),
    status: BlogPostStatusSchema.optional(),
    publishedAt: IsoDateSchema.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field must be provided',
  });
export type UpdateBlogPostInput = z.infer<typeof UpdateBlogPostInputSchema>;

// ---------------------------------------------------------------------------
// DTOs públicos (proyectados al locale del visitante)
// ---------------------------------------------------------------------------

export const BlogHeadingBlockPublicSchema = z.object({
  id: IdSchema,
  type: z.literal('heading'),
  text: z.string().min(1),
});

export const BlogTextBlockPublicSchema = z.object({
  id: IdSchema,
  type: z.literal('text'),
  html: z.string().min(1),
});

export const BlogImageBlockPublicSchema = z.object({
  id: IdSchema,
  type: z.literal('image'),
  image: ImageRefSchema,
  caption: z.string().optional(),
  href: LinkUrlSchema.optional(),
  newTab: z.boolean().optional(),
});

// Bloque de colección (ya resuelto): lo genera la API al proyectar.
// Lleva título, slug, imagen, eventos y agregados (eventCount,
// dateFrom/To, minPrice, currency) para que el front pueda renderizar
// el FeaturedCollection del blog.
export const BlogCollectionBlockPublicSchema = z.object({
  id: IdSchema,
  type: z.literal('collection'),
  title: z.string().min(1),
  slug: SlugSchema,
  image: ImageRefSchema.optional(),
  eventCount: z.number().int().nonnegative(),
  dateFrom: z.string().nullable(),
  dateTo: z.string().nullable(),
  minPrice: z.number().nonnegative().nullable(),
  currency: z.string().nullable(),
  events: z.array(EventPublicItemSchema).optional(),
});

// Bloque de artista (ya resuelto): lo genera la API al proyectar.
// Lleva id, nombre, slug e imagen del artista para que el front pueda
// renderizar la tarjeta de artista. La URL del detalle usa el slug si
// existe; si no, el id.
export const BlogArtistBlockPublicSchema = z.object({
  id: IdSchema,
  type: z.literal('artist'),
  name: z.string().min(1),
  slug: z.string().optional(),
  imageUrl: z.string().optional(),
});

// Bloque CTA (passthrough, sin resolución server-side).
export const BlogCtaBlockPublicSchema = z.object({
  id: IdSchema,
  type: z.literal('cta'),
  text: z.string().min(1),
  url: LinkUrlSchema,
  newTab: z.boolean(),
});

// Bloque YouTube (passthrough).
export const BlogYouTubeBlockPublicSchema = z.object({
  id: IdSchema,
  type: z.literal('youtube'),
  url: z.string().min(1),
});

// Bloque Spotify (passthrough).
export const BlogSpotifyBlockPublicSchema = z.object({
  id: IdSchema,
  type: z.literal('spotify'),
  url: z.string().min(1),
});

// Bloque X/Twitter (passthrough).
export const BlogTwitterBlockPublicSchema = z.object({
  id: IdSchema,
  type: z.literal('twitter'),
  url: z.string().min(1),
});

// Bloque TikTok (passthrough).
export const BlogTikTokBlockPublicSchema = z.object({
  id: IdSchema,
  type: z.literal('tiktok'),
  url: z.string().min(1),
});

// Bloque de evento (ya resuelto): lo genera la API al proyectar.
export const BlogEventBlockPublicSchema = z.object({
  id: IdSchema,
  type: z.literal('event'),
  title: z.string().min(1),
  slug: z.string().optional(),
  // Horizontal, para la caja ancha del bloque en mobile.
  imageUrl: z.string().optional(),
  // Cuadrada, para la caja cuadrada del bloque en desktop (BFMWEB-178).
  squareImageUrl: z.string().optional(),
  startAt: z.string().optional(),
  endAt: z.string().optional(),
  city: z.string().optional(),
  venue: z.string().optional(),
  minPrice: z.number().nonnegative().optional(),
  currency: z.string().optional(),
});

export const BlogBlockPublicSchema = z.discriminatedUnion('type', [
  BlogHeadingBlockPublicSchema,
  BlogTextBlockPublicSchema,
  BlogImageBlockPublicSchema,
  BlogCollectionBlockPublicSchema,
  BlogArtistBlockPublicSchema,
  BlogEventBlockPublicSchema,
  BlogCtaBlockPublicSchema,
  BlogYouTubeBlockPublicSchema,
  BlogSpotifyBlockPublicSchema,
  BlogTwitterBlockPublicSchema,
  BlogTikTokBlockPublicSchema,
]);
export type BlogCollectionBlockPublic = z.infer<typeof BlogCollectionBlockPublicSchema>;
export type BlogArtistBlockPublic = z.infer<typeof BlogArtistBlockPublicSchema>;
export type BlogEventBlockPublic = z.infer<typeof BlogEventBlockPublicSchema>;
export type BlogCtaBlockPublic = z.infer<typeof BlogCtaBlockPublicSchema>;
export type BlogYouTubeBlockPublic = z.infer<typeof BlogYouTubeBlockPublicSchema>;
export type BlogSpotifyBlockPublic = z.infer<typeof BlogSpotifyBlockPublicSchema>;
export type BlogTwitterBlockPublic = z.infer<typeof BlogTwitterBlockPublicSchema>;
export type BlogTikTokBlockPublic = z.infer<typeof BlogTikTokBlockPublicSchema>;
export type BlogBlockPublic = z.infer<typeof BlogBlockPublicSchema>;

/**
 * Detalle público de una entrada, proyectado al locale solicitado. Los
 * bloques vienen resueltos a string (con fallback por bloque al primer
 * idioma disponible) y los vacíos eliminados.
 */
export const BlogPostPublicSchema = z.object({
  slug: SlugSchema,
  type: BlogPostTypeSchema,
  title: z.string().min(1),
  subtitle: z.string().optional(),
  // Título SEO (para `<title>`/og:title) y resumen (para meta
  // description / og:description). El front cae al title/subtitle si
  // faltan.
  seoTitle: z.string().optional(),
  excerpt: z.string().optional(),
  featuredImage: ImageRefSchema.optional(),
  ogImage: ImageRefSchema.optional(),
  publishedAt: IsoDateSchema.nullable(),
  // Última modificación (para `article:modified_time` / dateModified).
  updatedAt: z.string(),
  // Locales en los que la entrada tiene título (para hreflang sin
  // cascada). Siempre incluye al menos el locale solicitado.
  availableLocales: z.array(z.enum(LOCALES)),
  blocks: z.array(BlogBlockPublicSchema),
});
export type BlogPostPublic = z.infer<typeof BlogPostPublicSchema>;

/**
 * Item del listado público (índice de blog + hero). Lleva las dos
 * imágenes porque el hero usa la destacada y las tarjetas el thumbnail.
 * `excerpt` es el resumen de la tarjeta (cae al subtítulo en la API si
 * no hay excerpt propio).
 */
export const BlogListItemPublicSchema = z.object({
  slug: SlugSchema,
  title: z.string().min(1),
  excerpt: z.string().optional(),
  featuredImage: ImageRefSchema.optional(),
  thumbnail: ImageRefSchema.optional(),
  publishedAt: IsoDateSchema.nullable(),
});
export type BlogListItemPublic = z.infer<typeof BlogListItemPublicSchema>;

/**
 * Respuesta del listado público. Paginada con el contrato genérico
 * (`items` + `page`/`pageSize`/`total`/`hasNextPage`) porque el índice
 * del blog carga por scroll infinito y el cliente necesita saber si
 * quedan más entradas. `total` cuenta las **visibles en el locale**
 * pedido (tras el filtro de título sin cascada), no las publicadas en
 * bruto: si no, el scroll pediría páginas vacías en `ca`/`en`.
 */
export const BlogListPublicResponseSchema = PaginatedResponseSchema(
  BlogListItemPublicSchema,
);
export type BlogListPublicResponse = z.infer<typeof BlogListPublicResponseSchema>;

/**
 * Query del listado público. `locale` obligatorio (proyecta los textos);
 * paginación simple opcional.
 */
export const BlogListPublicQuerySchema = z.object({
  locale: z.enum(LOCALES),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(50).default(20),
});
export type BlogListPublicQuery = z.infer<typeof BlogListPublicQuerySchema>;

/**
 * Query del detalle público por slug. `locale` obligatorio.
 */
export const BlogPostPublicQuerySchema = z.object({
  locale: z.enum(LOCALES),
});
export type BlogPostPublicQuery = z.infer<typeof BlogPostPublicQuerySchema>;

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

/**
 * Extrae el ID de un vídeo de YouTube desde cualquier formato de URL:
 *   - https://www.youtube.com/watch?v=XXXXX
 *   - https://youtu.be/XXXXX
 *   - https://www.youtube.com/embed/XXXXX
 *   - https://www.youtube.com/shorts/XXXXX
 * Devuelve `null` si no reconoce el formato.
 */
export function getYouTubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

/**
 * Tipos de contenido soportados por el embed de Spotify.
 */
export type SpotifyContentType = 'track' | 'album' | 'playlist' | 'episode' | 'show';

/**
 * Normaliza una URL de Spotify a su formato embed.
 * Soporta track, album, playlist, episode y show.
 * Si ya es embed, la devuelve tal cual.
 * Devuelve `null` si no reconoce el formato.
 *
 * Ejemplos aceptados:
 *   - https://open.spotify.com/track/ID
 *   - https://open.spotify.com/album/ID
 *   - https://open.spotify.com/playlist/ID
 *   - https://open.spotify.com/episode/ID
 *   - https://open.spotify.com/show/ID
 *   - https://open.spotify.com/embed/track/ID (ya embed)
 */
export function getSpotifyEmbedData(url: string): { embedUrl: string; type: SpotifyContentType } | null {
  const re = /open\.spotify\.com\/(?:embed\/)?(track|album|playlist|episode|show)\/([a-zA-Z0-9]+)/;
  const m = url.match(re);
  if (!m) return null;
  const type = m[1] as SpotifyContentType;
  const id = m[2];
  return { embedUrl: `https://open.spotify.com/embed/${type}/${id}`, type };
}

/**
 * Extrae usuario + id de un status de X/Twitter y normaliza la URL al
 * dominio `x.com` (el que devuelve hoy el propio oEmbed de la
 * plataforma). Acepta `twitter.com` y `x.com`, con o sin query string.
 * Devuelve `null` si no reconoce el formato.
 *
 * Ejemplos aceptados:
 *   - https://twitter.com/jack/status/20
 *   - https://x.com/jack/status/20?s=20
 */
export function getTwitterStatusData(
  url: string,
): { username: string; statusId: string; canonicalUrl: string } | null {
  const re = /(?:twitter\.com|x\.com)\/(\w+)\/status(?:es)?\/(\d+)/;
  const m = url.match(re);
  if (!m) return null;
  const [, username, statusId] = m;
  return { username, statusId, canonicalUrl: `https://x.com/${username}/status/${statusId}` };
}

/**
 * Extrae usuario + id de vídeo de una URL de TikTok. Solo soporta el
 * formato canónico `/@usuario/video/ID` (no resuelve enlaces cortos
 * `vm.tiktok.com`, que requieren un round-trip de red para expandirse).
 * Devuelve `null` si no reconoce el formato.
 *
 * Ejemplo aceptado:
 *   - https://www.tiktok.com/@scout2015/video/6718335390845095173
 */
export function getTikTokVideoData(url: string): { username: string; videoId: string } | null {
  const re = /tiktok\.com\/@([\w.-]+)\/video\/(\d+)/;
  const m = url.match(re);
  if (!m) return null;
  const [, username, videoId] = m;
  return { username, videoId };
}
